import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Handshake, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { submitPartnershipRequest } from "@/lib/partnerships.functions";

export const Route = createFileRoute("/partner")({
  component: PartnerPage,
  head: () => ({
    meta: [
      { title: "Torna-te parceiro Routify · Programa de afiliados" },
      {
        name: "description",
        content:
          "Candidata-te ao programa de parceiros da Routify. Recebe um código promocional exclusivo, comissões sobre vendas e uma dashboard própria.",
      },
      { property: "og:title", content: "Torna-te parceiro Routify" },
      {
        property: "og:description",
        content: "Programa de afiliados Routify — desconto para os teus seguidores, comissão para ti.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

const CRITERIA = [
  "Criadores de conteúdo ligados a viagens, turismo, gastronomia ou lifestyle.",
  "Perfis com conteúdo original e atividade regular.",
  "Comunidade real e envolvida.",
  "Comunicação compatível com a imagem da Routify.",
  "Disponibilidade para divulgar a aplicação de forma transparente.",
];

function PartnerPage() {
  const submitFn = useServerFn(submitPartnershipRequest);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [socialNetworks, setSocialNetworks] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [followersApprox, setFollowersApprox] = useState("");
  const [niche, setNiche] = useState("");
  const [avgViewsPerPost, setAvgViewsPerPost] = useState("");
  const [avgViewsStories, setAvgViewsStories] = useState("");
  const [audienceCountry, setAudienceCountry] = useState("");
  const [audienceAgeRange, setAudienceAgeRange] = useState("");
  const [mediaKitUrl, setMediaKitUrl] = useState("");
  const [pastPartnerships, setPastPartnerships] = useState("");
  const [promotionPlan, setPromotionPlan] = useState("");
  const [reason, setReason] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (name.trim().length < 2) return toast.error("Indica o teu nome.");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return toast.error("Email inválido.");
    if (!acceptedTerms) return toast.error("Tens de aceitar os Termos do Programa e a Política de Privacidade.");
    setSubmitting(true);
    try {
      const r = await submitFn({
        data: {
          name: name.trim(),
          email: email.trim(),
          country: country.trim() || null,
          socialNetworks: socialNetworks.trim() || null,
          instagramUrl: instagramUrl.trim() || null,
          tiktokUrl: tiktokUrl.trim() || null,
          youtubeUrl: youtubeUrl.trim() || null,
          followersApprox: followersApprox.trim() || null,
          niche: niche.trim() || null,
          reason: reason.trim() || null,
          avgViewsPerPost: avgViewsPerPost.trim() || null,
          avgViewsStories: avgViewsStories.trim() || null,
          audienceCountry: audienceCountry.trim() || null,
          audienceAgeRange: audienceAgeRange.trim() || null,
          mediaKitUrl: mediaKitUrl.trim() || null,
          pastPartnerships: pastPartnerships.trim() || null,
          promotionPlan: promotionPlan.trim() || null,
          acceptedTerms: true as const,
        },
      });
      if (r.ok) {
        setDone(true);
        toast.success(r.alreadyPending ? "Já temos um pedido teu em análise." : "Pedido enviado!");
      } else {
        toast.error(r.error ?? "Erro ao enviar pedido.");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao enviar pedido.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <header className="mt-4 mb-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium">
          <Handshake className="h-3.5 w-3.5" /> Programa de parceiros
        </div>
        <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Torna-te parceiro da Routify</h1>
        <p className="mt-2 text-muted-foreground">
          Se tens comunidade — Instagram, TikTok, YouTube, blog ou newsletter — junta-te ao nosso programa de afiliados.
          Recebes um código promocional exclusivo com desconto para os teus seguidores e comissão sobre cada venda.
        </p>
      </header>

      <section className="mb-6 grid gap-3 sm:grid-cols-3">
        {[
          { t: "Desconto exclusivo", d: "Código único com % de desconto para a tua comunidade." },
          { t: "Comissões recorrentes", d: "Ganhas em Premium, Pro, créditos e planeamento personalizado." },
          { t: "Dashboard própria", d: "Vê cliques, vendas e comissões em /affiliate." },
        ].map((f) => (
          <div key={f.t} className="rounded-2xl border border-border bg-card p-4">
            <Sparkles className="h-4 w-4 text-brand" />
            <div className="mt-2 text-sm font-semibold">{f.t}</div>
            <p className="text-xs text-muted-foreground">{f.d}</p>
          </div>
        ))}
      </section>

      {/* Quem procuramos */}
      <section className="mb-6 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-base font-semibold">Quem procuramos</h2>
        <ul className="mt-3 space-y-2">
          {CRITERIA.map((c) => (
            <li key={c} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
          Todas as candidaturas são analisadas individualmente. O número de seguidores não garante aprovação e não existe
          um mínimo obrigatório.
        </p>
      </section>

      {done ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-center">
          <h2 className="text-xl font-semibold">Obrigado! ✨</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Recebemos o teu pedido. A equipa Routify vai analisar e responder por email. Se for aprovado,
            recebes o teu código promocional e link de referência automaticamente.
          </p>
          <Link to="/" className="mt-4 inline-flex items-center gap-1 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground hover:opacity-90">
            Voltar ao início
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-border bg-card p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome *">
              <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120}
                className="input" placeholder="Ex.: Maria Silva" />
            </Field>
            <Field label="Email *">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255}
                className="input" placeholder="tu@exemplo.com" />
            </Field>
            <Field label="País">
              <input value={country} onChange={(e) => setCountry(e.target.value)} maxLength={80}
                className="input" placeholder="Portugal" />
            </Field>
            <Field label="Nº aproximado de seguidores">
              <input value={followersApprox} onChange={(e) => setFollowersApprox(e.target.value)} maxLength={60}
                className="input" placeholder="Ex.: 25k, 120k, 1M" />
            </Field>
            <Field label="Redes sociais (nomes/handles)" className="sm:col-span-2">
              <input value={socialNetworks} onChange={(e) => setSocialNetworks(e.target.value)} maxLength={240}
                className="input" placeholder="@meuinsta, @meutiktok, canal YouTube…" />
            </Field>
            <Field label="Instagram (link)">
              <input value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} maxLength={500}
                className="input" placeholder="https://instagram.com/…" />
            </Field>
            <Field label="TikTok (link)">
              <input value={tiktokUrl} onChange={(e) => setTiktokUrl(e.target.value)} maxLength={500}
                className="input" placeholder="https://tiktok.com/@…" />
            </Field>
            <Field label="YouTube (link)" className="sm:col-span-2">
              <input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} maxLength={500}
                className="input" placeholder="https://youtube.com/@…" />
            </Field>
            <Field label="Média aproximada de visualizações por publicação">
              <input value={avgViewsPerPost} onChange={(e) => setAvgViewsPerPost(e.target.value)} maxLength={60}
                className="input" placeholder="Ex.: 8k, 30k" />
            </Field>
            <Field label="Média aproximada de visualizações em stories">
              <input value={avgViewsStories} onChange={(e) => setAvgViewsStories(e.target.value)} maxLength={60}
                className="input" placeholder="Ex.: 2k, 15k" />
            </Field>
            <Field label="País principal da audiência">
              <input value={audienceCountry} onChange={(e) => setAudienceCountry(e.target.value)} maxLength={80}
                className="input" placeholder="Ex.: Portugal, Brasil" />
            </Field>
            <Field label="Faixa etária principal da audiência">
              <input value={audienceAgeRange} onChange={(e) => setAudienceAgeRange(e.target.value)} maxLength={60}
                className="input" placeholder="Ex.: 25–34" />
            </Field>
            <Field label="Nicho de conteúdo" className="sm:col-span-2">
              <input value={niche} onChange={(e) => setNiche(e.target.value)} maxLength={120}
                className="input" placeholder="Ex.: viagens, city breaks, mochileiros, luxo, foodie…" />
            </Field>
            <Field label="Link para Media Kit (opcional)" className="sm:col-span-2">
              <input value={mediaKitUrl} onChange={(e) => setMediaKitUrl(e.target.value)} maxLength={500}
                className="input" placeholder="https://…" />
            </Field>
            <Field label="Exemplos de parcerias anteriores (opcional)" className="sm:col-span-2">
              <textarea value={pastPartnerships} onChange={(e) => setPastPartnerships(e.target.value)} maxLength={2000} rows={3}
                className="input" placeholder="Marcas, campanhas, links de referência…" />
            </Field>
            <Field label="Como pretendes divulgar a Routify?" className="sm:col-span-2">
              <textarea value={promotionPlan} onChange={(e) => setPromotionPlan(e.target.value)} maxLength={2000} rows={3}
                className="input" placeholder="Formatos, frequência, ideias criativas…" />
            </Field>
            <Field label="Porque queres ser parceiro?" className="sm:col-span-2">
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={3}
                className="input" placeholder="Conta-nos brevemente porquê." />
            </Field>
          </div>

          <label className="flex items-start gap-2 rounded-xl border border-border bg-background/60 p-3 text-xs">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4"
              required
            />
            <span className="text-muted-foreground">
              Li e aceito os <Link to="/partner-terms" className="underline">Termos do Programa de Parceiros</Link>,
              os <Link to="/terms" className="underline">Termos e Condições</Link> e a{" "}
              <Link to="/privacy" className="underline">Política de Privacidade</Link>.
            </span>
          </label>

          <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
            <button type="submit" disabled={submitting || !acceptedTerms}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground hover:opacity-90 disabled:opacity-60">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Handshake className="h-4 w-4" />}
              Enviar candidatura
            </button>
          </div>
        </form>
      )}

      {/* Field styling lives in src/styles.css (.input) — tokens are OKLCH, so
          the old hsl(var(--border)) rules produced invisible fields. */}

    </main>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className ?? ""}`}>
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
